import type { UniqueIdentifier } from '@dnd-kit/core';
import type { AnimateLayoutChanges } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CSSProperties, useEffect, useState } from 'react';
import { ItemTypeChoices } from '../../../../__generated__/graphql';
import { iOS } from '../../../../helpers/dnd/utilities';
import { useAppSelector } from '../../../../helpers/reduxHooks';
import { FlattenedItem } from '../../../shared/dnd/types';
import {
  DefaultRowGroup,
  RODefaultRowGroup,
  DraggedRowGroup,
  EstimateRowGroup,
  ROEstimateRowGroup,
} from './group';
import {
  DefaultRowProduct,
  RODefaultRowProduct,
  DraggedRowProduct,
  EstimateRowProduct,
  ROEstimateRowProduct,
} from './product';
import {
  DefaultRowService,
  RODefaultRowService,
  DraggedRowService,
  EstimateRowService,
  ROEstimateRowService,
} from './service';

interface Props {
  childCount?: number;
  clone?: boolean;
  collapsed?: boolean;
  depth: number;
  item: FlattenedItem;
  id: UniqueIdentifier;
  indentationWidth: number;
  indicator?: boolean;
  onCollapse?(): void;
  hasDndContext?: boolean,
  quotationLength: number;
  initialIndex: number;
}

const animateLayoutChanges: AnimateLayoutChanges = ({ isSorting, wasDragging }) =>
  isSorting || wasDragging ? false : true;

export function QuotationListItem(props: Props) {
  const {
    childCount, clone, collapsed, depth, item, id, indentationWidth, hasDndContext,
    indicator, onCollapse, quotationLength, initialIndex } = props;
  const {
    attributes,
    isDragging,
    isSorting,
    listeners,
    setDraggableNodeRef,
    setDroppableNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges,
  });
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };


  const openedDeatilsItems = useAppSelector(state => state.quotation.openedDeatilsItems);

  const isDetailsShown = openedDeatilsItems.includes(id);
  const [showDetails, setShowDetails] = useState(isDetailsShown);
  const isEstimateMode = useAppSelector(state => state.quotation.isEstimateMode);
  const isQuotationReadOnly = useAppSelector(state => state.quotation.isQuotationReadOnly);

  useEffect(() => {
    setShowDetails(openedDeatilsItems.includes(id));    
  }, [id, openedDeatilsItems]);

  switch (item.itemType) {
    case ItemTypeChoices.Product:
      const productProps = {
        style, clone, depth, indentationWidth, indicator, item,
        ref: setDraggableNodeRef, showDetails, setShowDetails,
        wrapperRef: setDroppableNodeRef, hasDndContext,
        ghost: isDragging,
        disableSelection: iOS,
        disableInteraction: isSorting,
        handleProps: {
          ...attributes,
          ...listeners,
        },
      };
      let ProductComponent = DefaultRowProduct;
      if (clone) {
        ProductComponent = DraggedRowProduct;
      } else {
        if (isQuotationReadOnly && isEstimateMode) {
          ProductComponent = ROEstimateRowProduct;
        } else if (isQuotationReadOnly) {
          ProductComponent = RODefaultRowProduct;
        } else if (isEstimateMode) {
          ProductComponent = EstimateRowProduct;
        }
      }
      return <ProductComponent {...productProps} />;
    case ItemTypeChoices.Group:
      const groupProps = {
        collapsed, indentationWidth, depth, style, clone, indicator, childCount, onCollapse, item,
        ref: setDraggableNodeRef, showDetails, setShowDetails, hasDndContext,
        wrapperRef: setDroppableNodeRef,
        ghost: isDragging,
        disableSelection: iOS,
        disableInteraction: isSorting,
        handleProps: {
          ...attributes,
          ...listeners,
        },
        quotationLength,
        initialIndex,
      };
      let GroupComponent = DefaultRowGroup;
      if (clone) {
        GroupComponent = DraggedRowGroup;
      } else {
        if (isQuotationReadOnly && isEstimateMode) {
          GroupComponent = ROEstimateRowGroup;
        } else if (isQuotationReadOnly || item.group?.productionStatus?.isReadonly) {
          GroupComponent = RODefaultRowGroup;
        } else if (isEstimateMode) {
          GroupComponent = EstimateRowGroup;
        }
      }
      return <GroupComponent {...groupProps} />;
    case ItemTypeChoices.Service:
      const serviceProps = {
        style, clone, depth, indentationWidth, indicator, item, showDetails, setShowDetails, hasDndContext,
        ref: setDraggableNodeRef,
        wrapperRef: setDroppableNodeRef,
        ghost: isDragging,
        disableSelection: iOS,
        disableInteraction: isSorting,
        handleProps: {
          ...attributes,
          ...listeners,
        },
      };
      let ServiceComponent = DefaultRowService;
      if (clone) {
        ServiceComponent = DraggedRowService;
      } else {
        if (isQuotationReadOnly && isEstimateMode) {
          ServiceComponent = ROEstimateRowService;
        } else if (isQuotationReadOnly) {
          ServiceComponent = RODefaultRowService;
        } else if (isEstimateMode) {
          ServiceComponent = EstimateRowService;
        }
      }
      return <ServiceComponent {...serviceProps} />;
    default:
      return <></>;
  }
}
